import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Download, FileSignature, Link2Off, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { statusPill, type StatusTone } from '../../../design-system/visual';
import { formatDate } from '../../../design-system/format';
import { usePermission } from '../../../core/auth/usePermission';
import { useEnabledModules } from '../../../core/modules/EnabledModulesProvider';
import { esignApi } from '../../esign/api';
import type { EsignDocumentListRow } from '../../esign/types';
import { hrApi } from '../api';
import type { LinkedEsignDocument } from '../types';

/**
 * E-sign agreements linked to one employee — the read path that
 * `esign_recipients.employee_id` never had, plus the explicit
 * `related_type`/`related_id` attachment added in migration 085.
 *
 * Seal semantics are the point of this component, not a detail: a document
 * with `status: 'completed'` and NO `signature_number` is signed but NOT
 * sealed. The server refuses to hand out a "sealed PDF" for it (409
 * `seal_incomplete`) and the e-sign detail page banners it as "Signed, but not
 * sealed" — so it must not read as a clean "Signed" here either.
 */

const STATUS_TONE: Record<LinkedEsignDocument['status'], StatusTone> = {
  draft: 'draft',
  sent: 'info',
  completed: 'success',
  declined: 'destructive',
  voided: 'muted',
};

const STATUS_LABEL: Record<LinkedEsignDocument['status'], string> = {
  draft: 'Draft',
  sent: 'Out for signature',
  completed: 'Signed & sealed',
  declined: 'Declined',
  voided: 'Voided',
};

/** Same rule as `sealFailed` on the e-sign detail page — kept identical on purpose. */
export function isSealFailed(d: Pick<LinkedEsignDocument, 'status' | 'signature_number'>): boolean {
  return d.status === 'completed' && !d.signature_number;
}

function statusChip(d: LinkedEsignDocument) {
  if (isSealFailed(d)) {
    return (
      <span className={statusPill('warning')} data-testid={`agreement-status-${d.id}`}>
        Signed, but not sealed
      </span>
    );
  }
  return (
    <span className={statusPill(STATUS_TONE[d.status])} data-testid={`agreement-status-${d.id}`}>
      {STATUS_LABEL[d.status]}
    </span>
  );
}

function linkSummary(d: LinkedEsignDocument): string {
  const parts: string[] = [];
  if (d.recipient_id) {
    parts.push(d.recipient_role === 'cc' ? 'Copied in' : `Signer · ${d.recipient_status ?? 'pending'}`);
  }
  if (d.linked_record) parts.push('Filed on this record');
  return parts.join(' · ');
}

export function EmployeeAgreementsPanel({ employeeId }: { employeeId: string }) {
  const canViewEsign = usePermission('esign.view');
  const canLink = usePermission('esign.send');
  const { modules } = useEnabledModules();
  const esignEnabled = modules.has('esign');

  const [docs, setDocs] = React.useState<LinkedEsignDocument[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [attachOptions, setAttachOptions] = React.useState<EsignDocumentListRow[]>([]);
  const [attachId, setAttachId] = React.useState('');
  const [attaching, setAttaching] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    hrApi.listEmployeeEsignDocuments(employeeId)
      .then(setDocs)
      .catch((err: any) => toast.error('Could not load agreements', { description: err.message }))
      .finally(() => setLoading(false));
  }, [employeeId]);

  React.useEffect(() => { load(); }, [load]);

  // Only fetched when the caller could actually attach something — this is the
  // one place the new related_type/related_id link is written from the UI.
  React.useEffect(() => {
    if (!canLink || !esignEnabled) return;
    esignApi.listDocuments({ limit: 50 })
      .then((p) => setAttachOptions(p.data))
      .catch(() => setAttachOptions([]));
  }, [canLink, esignEnabled]);

  async function download(d: LinkedEsignDocument, opts: { original?: boolean } = {}) {
    setDownloadingId(d.id);
    try {
      // Server-side proxy: the blob url never reaches the browser, and the
      // server explains a refused seal instead of returning bare bytes.
      const url = await hrApi.downloadEmployeeEsignPdf(employeeId, d.id, opts);
      window.open(url, '_blank', 'noopener');
    } catch (err: any) {
      toast.error('Download failed', { description: err.message });
    } finally {
      setDownloadingId(null);
    }
  }

  async function attach() {
    if (!attachId) { toast.error('Choose an agreement first'); return; }
    setAttaching(true);
    try {
      await esignApi.setRelated(attachId, { related_type: 'employee', related_id: employeeId });
      toast.success('Agreement linked to this employee');
      setAttachId('');
      load();
    } catch (err: any) {
      toast.error('Could not link the agreement', { description: err.message });
    } finally { setAttaching(false); }
  }

  async function detach(d: LinkedEsignDocument) {
    try {
      await esignApi.setRelated(d.id, { related_type: null, related_id: null });
      toast.success('Agreement unlinked');
      load();
    } catch (err: any) {
      toast.error('Could not unlink the agreement', { description: err.message });
    }
  }

  const alreadyLinked = new Set((docs ?? []).map((d) => d.id));
  const attachable = attachOptions.filter((d) => !alreadyLinked.has(d.id));

  return (
    <div data-testid="employee-agreements">
      {canLink && esignEnabled && attachable.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-5 pb-5 border-b border-border/40 items-end">
          <div className="grid gap-1.5">
            <Label>Link an existing agreement</Label>
            <Select value={attachId} onValueChange={setAttachId}>
              <SelectTrigger><SelectValue placeholder="Choose an agreement" /></SelectTrigger>
              <SelectContent>
                {attachable.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={attach}
            disabled={attaching || !attachId}
            className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            {attaching ? 'Linking…' : 'Link'}
          </Button>
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-muted-foreground">Loading agreements…</p>
      ) : !docs || docs.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">No e-sign agreements linked to this employee.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const sealFailed = isSealFailed(d);
            return (
              <li key={d.id} className="rounded-xl ring-1 ring-border/40 px-4 py-3">
                <div className="flex items-center gap-3">
                  <FileSignature className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-medium text-foreground truncate">
                      {canViewEsign ? (
                        <Link to={`/dashboard/esign/${d.id}`} className="hover:underline underline-offset-2">
                          {d.title}
                        </Link>
                      ) : d.title}
                    </p>
                    <p className="text-[11.5px] text-muted-foreground mt-0.5 truncate">
                      {linkSummary(d)}
                      {d.completed_at && ` · Completed ${formatDate(d.completed_at)}`}
                      {d.signature_number && ` · ${d.signature_number}`}
                    </p>
                  </div>
                  {statusChip(d)}
                  {/* No "download the executed copy" button when there is no
                      executed copy — the server would 409 seal_incomplete. */}
                  {!sealFailed && d.status !== 'draft' && (
                    <button
                      type="button"
                      onClick={() => download(d)}
                      disabled={downloadingId === d.id}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                      aria-label={`Download ${d.title}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {canLink && d.linked_record && (
                    <button
                      type="button"
                      onClick={() => detach(d)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      aria-label={`Unlink ${d.title}`}
                    >
                      <Link2Off className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {sealFailed && (
                  <div
                    data-testid={`agreement-seal-warning-${d.id}`}
                    className="mt-3 rounded-lg bg-destructive/5 ring-1 ring-destructive/25 px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      <span className="text-[12.5px] font-semibold text-foreground">Signed, but not sealed</span>
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                      Every signer has signed and their signatures are recorded, but the
                      certificate-stamped PDF was never built — there is no sealed copy and no
                      verification number yet.
                      {d.seal_error && <> Reason: <span className="font-mono text-[11.5px]">{d.seal_error}</span></>}
                      {canViewEsign && <> Open the agreement to retry sealing.</>}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 gap-1.5"
                      onClick={() => download(d, { original: true })}
                      disabled={downloadingId === d.id}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download unsigned original
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
